import React, { useEffect, useRef, useState } from 'react';
import {
    X,
    Copy,
    Check,
    CheckCircle2,
    AlertCircle,
    XCircle,
    Download,
    Music2,
    ArrowRight,
    Clock,
    Coins,
    User,
    FileAudio,
} from 'lucide-react';

const AUDIO_EXT_RE = /\.(mp3|wav|ogg|m4a|aac|flac|webm)$/i;

const isAudioUrl = (value) => {
    if (!value || typeof value !== 'string') return false;
    if (!/^https?:\/\//i.test(value)) return false;
    return AUDIO_EXT_RE.test(value.split('?')[0]);
};

const getAudioFormat = (url) => {
    const match = url?.split('?')[0]?.match(AUDIO_EXT_RE);
    return match ? match[1].toUpperCase() : 'AUDIO';
};

const formatResponseTime = (ms) => {
    if (ms == null) return '—';
    return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`;
};

const formatEndpoint = (log) =>
    (log.endpoint || log?.successDetails?.apiEndpoint || '')
        .replace(/^https?:\/\/[^/]+/, '')
        .replace(/^\/ttt\/v1/, '')
        .replace(/^\/api\/v1/, '') || '/';

const AudioPlayerCard = ({ label, lang, url, accent = 'emerald' }) => {
    const format = getAudioFormat(url);
    const accents = {
        emerald: {
            wrap: 'from-emerald-50/80 to-white border-emerald-100',
            badge: 'bg-emerald-100 text-emerald-800',
            icon: 'bg-emerald-100 text-emerald-700',
            ring: 'focus-within:ring-emerald-200',
        },
        amber: {
            wrap: 'from-amber-50/80 to-white border-amber-100',
            badge: 'bg-amber-100 text-amber-800',
            icon: 'bg-amber-100 text-amber-700',
            ring: 'focus-within:ring-amber-200',
        },
    };
    const a = accents[accent] || accents.emerald;

    return (
        <div className={`rounded-2xl border bg-gradient-to-br p-4 space-y-3 ${a.wrap} ${a.ring} focus-within:ring-2 transition-shadow`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${a.icon}`}>
                        <Music2 className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900">{label}</p>
                        {lang && (
                            <p className="text-xs text-gray-500 truncate mt-0.5">{lang}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-md ${a.badge}`}>
                        {format}
                    </span>
                    <a
                        href={url}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Download audio"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-[#075038] hover:bg-white/80 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                    </a>
                </div>
            </div>
            <audio
                controls
                preload="metadata"
                className="w-full h-10 accent-[#075038]"
                src={url}
            >
                Your browser does not support the audio element.
            </audio>
        </div>
    );
};

const TextBlock = ({ label, value, emptyLabel }) => (
    <div>
        <label className="text-xs font-semibold text-gray-400 block mb-1.5 uppercase tracking-wide">
            {label}
        </label>
        <div className="bg-gray-50 rounded-xl p-3.5 text-sm text-gray-700 min-h-[48px] border border-gray-100 leading-relaxed break-words">
            {value || emptyLabel}
        </div>
    </div>
);

const MetaItem = ({ icon: Icon, label, children }) => (
    <div className="space-y-1">
        <label className="text-[11px] font-medium text-gray-400 flex items-center gap-1.5 uppercase tracking-wide">
            {Icon && <Icon className="w-3 h-3" />}
            {label}
        </label>
        <div className="text-[13px] font-semibold text-gray-900">{children}</div>
    </div>
);

const LogDetailModal = ({ isOpen, onClose, log }) => {
    const modalRef = useRef(null);
    const [copied, setCopied] = useState(false);
    const [showRaw, setShowRaw] = useState(false);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
            setCopied(false);
            setShowRaw(false);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen || !log) return null;

    const inputIsAudio = isAudioUrl(log.input);
    const outputIsAudio = isAudioUrl(log.output);
    const bothAudio = inputIsAudio && outputIsAudio;

    const statusLower = String(log.status).toLowerCase();
    const isSuccess = statusLower === 'success' || (Number(log.status) >= 200 && Number(log.status) < 300);
    const isWarning = statusLower === 'warning' || Number(log.status) === 429;

    const getStatusBadge = () => {
        if (isSuccess) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Success
                </span>
            );
        }
        if (isWarning) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-50 text-orange-600 text-xs font-bold">
                    <AlertCircle className="w-3.5 h-3.5" /> Warning
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-xs font-bold">
                <XCircle className="w-3.5 h-3.5" /> Error
            </span>
        );
    };

    const handleCopy = async () => {
        await navigator.clipboard.writeText(JSON.stringify(log, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const userName = log.user?.name;
    const userEmail = log.user?.email;
    const userId = log.user?.id || log.userId;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                ref={modalRef}
                className="bg-white w-full max-w-2xl max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4 bg-white sticky top-0 z-10">
                    <div className="min-w-0 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-xl font-bold text-gray-900">Log Details</h2>
                            {getStatusBadge()}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
                            {log.type && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#E8F8F0] text-[#1C5D42] font-semibold">
                                    <FileAudio className="w-3 h-3" />
                                    {log.type}
                                </span>
                            )}
                            <span className="font-mono text-gray-400 truncate max-w-[280px]" title={formatEndpoint(log)}>
                                {formatEndpoint(log)}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-900 shrink-0"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Meta grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-5 gap-x-6 rounded-2xl bg-gray-50/80 border border-gray-100 p-4">
                        <MetaItem icon={Clock} label="Timestamp">
                            {new Date(log.createdAt || log.date).toLocaleString()}
                        </MetaItem>
                        <MetaItem label="Response Time">
                            {formatResponseTime(log.responseTime)}
                        </MetaItem>
                        <MetaItem icon={Coins} label="Tokens Used">
                            {log.tokensUsed ?? 0}
                        </MetaItem>
                        <MetaItem icon={User} label="User">
                            <div className="truncate" title={userEmail || userId}>
                                {userName || userEmail || userId || '—'}
                            </div>
                            {userName && userEmail && (
                                <div className="text-[11px] font-normal text-gray-400 truncate mt-0.5">{userEmail}</div>
                            )}
                        </MetaItem>
                        <MetaItem label="Source">
                            {log.sourceLang || 'Detect Language'}
                        </MetaItem>
                        <MetaItem label="Target">
                            {log.targetLang || 'N/A'}
                        </MetaItem>
                    </div>

                    {/* Media / text content */}
                    <div className="space-y-3">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            {bothAudio || inputIsAudio || outputIsAudio ? 'Audio' : 'Translation'}
                        </h3>

                        {bothAudio ? (
                            <div className="space-y-3">
                                <div className="grid grid-cols-1 gap-3">
                                    <AudioPlayerCard
                                        label="Input"
                                        lang={log.sourceLang}
                                        url={log.input}
                                        accent="emerald"
                                    />
                                    <div className="flex justify-center -my-1">
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                                            <ArrowRight className="w-4 h-4 rotate-90" />
                                        </div>
                                    </div>
                                    <AudioPlayerCard
                                        label="Output"
                                        lang={log.targetLang}
                                        url={log.output}
                                        accent="amber"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {inputIsAudio ? (
                                    <AudioPlayerCard
                                        label="Input Audio"
                                        lang={log.sourceLang}
                                        url={log.input}
                                        accent="emerald"
                                    />
                                ) : (
                                    <TextBlock label="Input" value={log.input} emptyLabel="No input content" />
                                )}
                                {outputIsAudio ? (
                                    <AudioPlayerCard
                                        label="Output Audio"
                                        lang={log.targetLang}
                                        url={log.output}
                                        accent="amber"
                                    />
                                ) : (
                                    <TextBlock label="Output" value={log.output} emptyLabel="No output content" />
                                )}
                            </div>
                        )}
                    </div>

                    {/* Raw Log Data — collapsed by default */}
                    <div className="pt-2 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => setShowRaw((v) => !v)}
                            className="w-full flex items-center justify-between py-2 group"
                        >
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider group-hover:text-gray-700 transition-colors">
                                Raw Log Data
                            </h3>
                            <span className="text-xs font-semibold text-[#075038]">
                                {showRaw ? 'Hide' : 'Show'}
                            </span>
                        </button>
                        {showRaw && (
                            <div className="space-y-3 mt-2 animate-in fade-in duration-150">
                                <div className="flex justify-end">
                                    <button
                                        onClick={handleCopy}
                                        className="flex items-center gap-1.5 text-xs font-bold text-[#075038] hover:underline"
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="w-3.5 h-3.5" />
                                                Copied
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-3.5 h-3.5" />
                                                Copy JSON
                                            </>
                                        )}
                                    </button>
                                </div>
                                <div className="bg-gray-900 rounded-2xl p-4 overflow-x-auto max-h-[280px]">
                                    <pre className="text-[11px] font-mono text-gray-300 leading-relaxed">
                                        {JSON.stringify(log, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end bg-white">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-[#075038] text-white rounded-xl hover:bg-[#075038]/90 transition-all font-bold text-sm shadow-sm"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LogDetailModal;
